'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { Badge } from '@/components/ui/app-badge';
import { Icon } from '@/components/ui/icon';
import { Tick02Icon } from '@/components/ui/icon/glyphs';
// keeps raw Input because: dialog-internal search filter; no label/description/error
import { InputField } from '@/components/ui/input-field';
import { CURRENCY_LIST } from '@/config/currencies';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useCurrencyStore } from '@/stores/currency';

/**
 * Headless selection state for the currency picker. Owned by the wrapper that
 * renders the SilkSheet footer (currently `LocaleLocationModal`).
 */
export interface CurrencySelectionState {
  currentCurrency: string;
  selected: string;
  setSelected: (code: string) => void;
  search: string;
  setSearch: (s: string) => void;
  hasChange: boolean;
  apply: (onClose: () => void) => void;
}
export function useCurrencySelection() {
  const $ = _c(11);
  const t = useTranslations("currency");
  const currency = useCurrencyStore(_temp);
  const setCurrency = useCurrencyStore(_temp2);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(currency);
  let t0;
  if ($[0] !== currency || $[1] !== selected || $[2] !== setCurrency || $[3] !== t) {
    t0 = function apply(onClose) {
      if (selected === currency) {
        onClose();
        return;
      }
      setCurrency(selected);
      onEvent("currency_changed", {
        from: currency,
        to: selected
      });
      showToast({
        id: TOAST_IDS.CURRENCY_UPDATED,
        message: t("success"),
        variant: "success"
      });
      setSearch("");
      onClose();
    };
    $[0] = currency;
    $[1] = selected;
    $[2] = setCurrency;
    $[3] = t;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const apply = t0;
  const t1 = selected !== currency;
  let t2;
  if ($[5] !== apply || $[6] !== currency || $[7] !== search || $[8] !== selected || $[9] !== t1) {
    t2 = {
      currentCurrency: currency,
      selected,
      setSelected,
      search,
      setSearch,
      hasChange: t1,
      apply
    };
    $[5] = apply;
    $[6] = currency;
    $[7] = search;
    $[8] = selected;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function _temp2(s_0) {
  return s_0.setCurrency;
}
function _temp(s) {
  return s.currency;
}
export interface CurrencySelectorContentProps {
  state: CurrencySelectionState;
}
const currencyDisplayNamesCache: Map<string, Intl.DisplayNames> = new Map();
function getCurrencyDisplayNames(locale: string): Intl.DisplayNames {
  const cached = currencyDisplayNamesCache.get(locale);
  if (cached !== undefined) return cached;
  const displayNames = new Intl.DisplayNames([locale], {
    type: 'currency'
  });
  currencyDisplayNamesCache.set(locale, displayNames);
  return displayNames;
}

/** A currency is selectable only when it has a live USDC exchange rate. USD is
 *  the base currency and is always selectable. */
function hasLiveRate(code: string, rates: Record<string, number> | undefined): boolean {
  return code === 'USD' || (rates?.[code] ?? 0) > 0;
}

/** Controlled body for currency selection (no Dialog, no action button). */
export function CurrencySelectorContent(t0) {
  const $ = _c(31);
  const {
    state
  } = t0;
  const t = useTranslations("currency");
  const locale = useLocale();
  const {
    selected,
    setSelected,
    search,
    setSearch
  } = state;
  const {
    rates
  } = useExchangeRates();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== locale || $[1] !== rates || $[2] !== search || $[3] !== selected || $[4] !== setSearch || $[5] !== setSelected || $[6] !== t) {
    const displayNames = getCurrencyDisplayNames(locale);
    const filtered = search.length === 0 ? CURRENCY_LIST : CURRENCY_LIST.filter(c => {
      const q = search.toLowerCase();
      const localizedName = displayNames.of(c.code)?.toLowerCase() ?? "";
      return c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q) || localizedName.includes(q);
    });
    let t6;
    if ($[12] !== t) {
      t6 = t("search_placeholder");
      $[12] = t;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t) {
      t7 = t("search_placeholder");
      $[14] = t;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== setSearch) {
      t8 = e => {
        setSearch(e.target.value);
      };
      $[16] = setSearch;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    if ($[18] !== search || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
      t5 = <InputField placeholder={t6} aria-label={t7} value={search} onChange={t8} containerClassName="my-2" />;
      $[18] = search;
      $[19] = t6;
      $[20] = t7;
      $[21] = t8;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    t1 = "h-64 overflow-y-auto rounded-control border bg-background-primary";
    t2 = "listbox";
    t3 = filtered.length === 0 && <p className="flex h-full items-center justify-center px-4 text-center text-body text-foreground-secondary">{t("no_results")}</p>;
    t4 = filtered.map(c_0 => {
      const isSelected = c_0.code === selected;
      const hasRate = hasLiveRate(c_0.code, rates);
      const localizedName_0 = displayNames.of(c_0.code) ?? c_0.name;
      return <button key={c_0.code} type="button" role="option" aria-selected={isSelected} aria-disabled={!hasRate} disabled={!hasRate} className={cn("flex w-full items-center gap-3 px-3 py-2 text-left text-body transition-colors", hasRate ? "hover:bg-background-tertiary" : "cursor-not-allowed text-foreground-disabled")} onClick={() => {
        if (!hasRate) {
          return;
        }
        setSelected(c_0.code);
      }}><span className="w-8 shrink-0 font-mono text-caption text-foreground-secondary">{c_0.symbol}</span><span className="flex-1">{localizedName_0} ({c_0.code})</span>{!hasRate && <Badge variant="outline">{t("noRate")}</Badge>}{isSelected && hasRate && <Icon icon={Tick02Icon} className="size-4 text-foreground-primary" aria-hidden="true" />}</button>;
    });
    $[0] = locale;
    $[1] = rates;
    $[2] = search;
    $[3] = selected;
    $[4] = setSearch;
    $[5] = setSelected;
    $[6] = t;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[23] !== t1 || $[24] !== t2 || $[25] !== t3 || $[26] !== t4) {
    t6 = <div className={t1} role={t2}>{t3}{t4}</div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== t5 || $[29] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return t7;
}